import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Parallax, Reveal, ChapterLabel, scrollTo, EASE } from "./Motion";
import { EDITORIAL } from "../../data/content";
import { BetaForm } from "../BetaForm";
import { WaitlistCounter } from "../WaitlistCounter";

const WHY_JOIN = [
  "Help shape the future of HEARD.",
  "Early access to new features.",
  "Opportunity to provide direct feedback.",
  "Be part of the original Founding Community.",
];

export const Ch8Final = () => {
  const reduce = useReducedMotion();
  return (
    <>
      <section id="chapter-final" className="relative w-full overflow-hidden">
        <Parallax src={EDITORIAL.finalConvo} alt="A diverse group of people together in warm conversation" className="absolute inset-0 h-full w-full" strength={6} />
        <div className="absolute inset-0 bg-charcoal/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/55 to-charcoal/45" />

        <div className="relative z-10 min-h-[94svh] mx-auto max-w-[1400px] px-6 sm:px-10 py-28 sm:py-32 flex flex-col items-center justify-center text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
            <ChapterLabel index="Welcome Home" tone="cream" />
          </motion.div>
          <motion.h2
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 32 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.2 }}
            className="serif-display mt-8 max-w-4xl text-[2.4rem] sm:text-[3.6rem] lg:text-[4.4rem] font-light text-cream"
          >
            Welcome Home.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.1, delay: 0.45 }}
            className="mt-8 max-w-2xl text-base sm:text-xl font-light leading-relaxed text-cream/90"
          >
            Every journey is unique, but no one should have to walk it alone.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-5 max-w-2xl text-base sm:text-lg font-normal sm:font-light leading-relaxed text-cream/75"
          >
            Whether you're celebrating life's victories, navigating challenges, looking for encouragement, or simply
            seeking connection, you'll find a compassionate community here. At HEARD, every voice matters, every story
            has value, and everyone deserves to feel seen, supported, and heard.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.38 }}
            className="mt-7 text-sm uppercase tracking-[0.28em] text-cream/80"
          >
            Welcome to your community.
          </motion.p>
          <motion.button
            onClick={() => scrollTo("beta")}
            data-testid="final-join-beta"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.42 }}
            className="mt-11 rounded-full bg-cream px-10 py-4 text-sm uppercase tracking-[0.2em] text-charcoal transition-all duration-500 hover:bg-sage hover:text-white hover:scale-[1.03]"
          >
            Start My Journey
          </motion.button>
        </div>
      </section>

      <section id="beta" className="bg-cream py-24 sm:py-36">
        <div className="mx-auto max-w-[1100px] px-6 sm:px-10 grid gap-16 lg:grid-cols-2 items-center">
          <Reveal>
            <ChapterLabel index="An Invitation" />
            <p className="mt-7 text-sm uppercase tracking-[0.22em] text-sage">Join a limited group helping shape the future of HEARD.</p>
            <h3 className="serif-display mt-4 text-[2rem] sm:text-[2.7rem] font-light text-charcoal leading-[1.1]">Reserve your spot.</h3>
            <p className="mt-6 max-w-md text-lg font-normal sm:font-light leading-relaxed text-ink/70">
              We're welcoming a small circle of thoughtful people to help build a more compassionate community. If that's you, we'd love to have you.
            </p>
            <WaitlistCounter variant="section" />
          </Reveal>
          <Reveal delay={0.12}>
            <BetaForm />
          </Reveal>
        </div>

        <div className="mx-auto mt-20 max-w-[1100px] px-6 sm:px-10">
          <Reveal>
            <div className="rounded-[1.75rem] border border-line bg-white/60 px-7 py-9 sm:px-12 sm:py-11" data-testid="why-join-early">
              <p className="text-sm uppercase tracking-[0.22em] text-sage">Why join early?</p>
              <ul className="mt-7 grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
                {WHY_JOIN.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage-deep">
                      <Check size={14} strokeWidth={2.4} />
                    </span>
                    <span className="text-base font-light leading-relaxed text-ink/85">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};
